/* eslint-disable no-param-reassign */
import {
  PerformanceTestConfigurationDto,
  TestSessionInstrumentDto,
  UpdatePerformanceToInstrumentInput,
} from 'modules/InstrumentsQualityControl/models';
import { formatShortDate } from 'modules/Common/utilities';
import moment from 'moment';
import ConditionalField from '../widgets/ConditionalField';
import ReviewDisplayField from '../widgets/ReviewDisplayField';
import ReviewDisplayTextField from '../widgets/ReviewDisplayTextField';
import AlertWidget from '../widgets/AlertWidget';
import { formatBool } from '../utils';
import PerformanceTestLogFileWidget from '../widgets/PerformanceTestLogFileWidget';
import PerformanceTestReplicateIdWidget from '../widgets/PerformanceTestReplicateIdWidget';
import PerformanceTestTestWidget from '../widgets/PerformanceTestTestWidget';

function noop<T>(id: T) {
  return id;
}

export const schema = sch => {
  sch.properties.reviewComment = {
    type: 'string',
    title: 'Comment',
  };

  sch.properties.flexReworkInstrument.type = 'string';
  sch.properties.piezoMagnetCheckOutcome.properties.qualityEventRequired.type = 'string';
  sch.properties.performanceTestOutcome.properties.qualityEventRequired.type = 'string';
  sch.properties.auditLogOutcome.properties.qualityEventRequired.type = 'string';
  sch.properties.releasePreparation.properties.anyErrorsDuringInstrumentBootUpAfterFactoryReset.type =
    'string';

  return sch;
};

export const uiSchema = ui => {
  ui['ui:order'] = [
    'reviewComment',
    '*',
    'logFile',
    'piezoMagnetCheckOutcome',
    'auditLogOutcome',
    'plaqueCalibration',
    'performanceTestOutcome',
    'releasePreparation',
  ];
  ui.reviewComment = {
    'ui:field': ConditionalField,
    predicate: data => !!data.reviewComment,
    'ui:widget': AlertWidget,
    'ui:options': {
      severity: 'warning',
    },
  };
  ui.instrumentId = {
    'ui:widget': 'hidden',
  };
  ui.flexReworkInstrument = {
    'ui:widget': ReviewDisplayTextField,
    format: formatBool,
  };

  ui.piezoMagnetCheckOutcome = {
    'ui:card': true,

    outcome: {
      'ui:grid:xs': 6,
      'ui:field': ReviewDisplayField,
    },
    qualityEventRequired: {
      'ui:grid:xs': 6,
      format: formatBool,
      'ui:widget': ReviewDisplayTextField,
    },
    qualityEventNumber: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values =>
        values.piezoMagnetCheckOutcome?.qualityEventRequired?.toString() === 'true',
    },
    hasQualityEvents: {
      'ui:widget': 'hidden',
    },
  };

  ui.tests = {
    'ui:card': true,
    'ui:field': PerformanceTestTestWidget,
    'ui:options': {
      addable: false,
      removable: false,
      orderable: false,
    },
    items: {
      'ui:card': false,
      'ui:grid:xs': 12,
      'ui:order': [
        'anyErrorsDuringInstrumentBootUp',
        'errorCodes',
        'comments',
        'replicateCount',
        '*',
      ],
      anyErrorsDuringInstrumentBootUp: {
        'ui:field': ReviewDisplayTextField,
        format: formatBool,
      },
      errorCodes: {
        items: {
          'ui:widget': ReviewDisplayTextField,
        },
        'ui:options': {
          addable: false,
          removable: false,
          orderable: false,
        },
        'ui:helpText': 'Outside of testing',
        'ui:description': 'Outside of testing',
        'ui:field': ConditionalField,
      },
      comments: {},
      replicates: {
        'ui:field': PerformanceTestReplicateIdWidget,
        'ui:options': {
          readOnly: true,
        },
      },
      status: {
        'ui:widget': ReviewDisplayField,
      },
    },
  };

  ui.performanceTestOutcome = {
    'ui:card': true,
    qualityEventRequired: {
      format: formatBool,
      'ui:widget': ReviewDisplayTextField,
    },
    qualityEventNumber: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values =>
        values.performanceTestOutcome?.qualityEventRequired?.toString() === 'true',
    },
    comments: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values =>
        values.performanceTestOutcome?.qualityEventRequired?.toString() === 'true',
    },
    plannedDeviationNumbers: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values => values.performanceTestOutcome?.plannedDeviationNumbers?.length > 0,
      'ui:options': {
        addable: false,
        removable: false,
        orderable: false,
      },
      items: {
        'ui:widget': ReviewDisplayTextField,
      },
    },
    hasQualityEvents: {
      'ui:widget': 'hidden',
    },
  };

  ui.auditLogOutcome = {
    'ui:card': true,
    firstMagnetSelfTestAdcValue: {
      'ui:grid:xs': 5,
      'ui:widget': ReviewDisplayTextField,
    },
    lastMagnetSelfTestAdcValue: {
      'ui:grid:xs': 5,
      'ui:widget': ReviewDisplayTextField,
    },
    qualityEventRequired: {
      'ui:widget': ReviewDisplayTextField,
      format: formatBool,
    },
    qualityEventNumber: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values => values.auditLogOutcome?.qualityEventRequired?.toString() === 'true',
    },
    comments: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values => values.auditLogOutcome?.qualityEventRequired?.toString() === 'true',
    },
    plannedDeviationNumbers: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values => values.auditLogOutcome?.plannedDeviationNumbers?.length > 0,
      'ui:options': {
        addable: false,
        removable: false,
        orderable: false,
      },
      items: {
        'ui:widget': ReviewDisplayTextField,
      },
    },
    difference: {
      'ui:grid:xs': 2,
      'ui:widget': ReviewDisplayTextField,
    },
    outcome: {
      'ui:widget': ReviewDisplayField,
    },
  };

  ui.releasePreparation = {
    'ui:card': true,
    factoryResetDate: { 'ui:widget': ReviewDisplayField, format: value => formatShortDate(value) },
    anyErrorsDuringInstrumentBootUpAfterFactoryReset: {
      'ui:widget': ReviewDisplayTextField,
      format: formatBool,
    },
    errorCodes: {
      items: {
        'ui:widget': ReviewDisplayTextField,
      },
      'ui:options': {
        addable: false,
        removable: false,
        orderable: false,
      },
      'ui:helpText': 'Outside of testing',
      'ui:description': 'Outside of testing',
      'ui:field': ConditionalField,
      predicate: values =>
        values?.releasePreparation?.anyErrorsDuringInstrumentBootUpAfterFactoryReset?.toString() ===
        'true',
    },
    comments: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayTextField,
      predicate: values =>
        values?.releasePreparation?.anyErrorsDuringInstrumentBootUpAfterFactoryReset?.toString() ===
        'true',
    },
    decontaminationDate: {
      'ui:widget': ReviewDisplayField,
      format: value => formatShortDate(value),
    },
    cleanDate: { 'ui:widget': ReviewDisplayField, format: value => formatShortDate(value) },
    carriedOutBy: {
      'ui:widget': 'hidden',
    },
    verifiedBy: {
      'ui:widget': 'hidden',
    },
  };

  ui.logFile = {
    'ui:field': PerformanceTestLogFileWidget,
    'ui:options': {
      readOnly: true,
    },
    options: {
      readOnly: true,
    },
  };

  ui.plaqueCalibration = {
    'ui:card': true,
    calibrationValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayTextField,
      format: value => value,
    },
    meanDifferenceValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayTextField,
      format: value => value,
    },
    biasValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayTextField,
      format: value => value,
    },
  };

  return ui;
};

export const mapToEdit = () => (
  current: TestSessionInstrumentDto,
  config?: PerformanceTestConfigurationDto
): UpdatePerformanceToInstrumentInput => {
  const performanceTest = current?.performanceTest || {};
  return {
    ...performanceTest,
    tests: config?.performanceTestTypeAndSamples?.map(x => {
      return {
        ...x,
        ...(performanceTest?.tests?.find(t => t.id === x.id) || {}),
      };
    }),
    instrumentId: current?.id,
  } as UpdatePerformanceToInstrumentInput;
};

export const fields = noop;

export const widgets = noop;

export const validation = () => null;

export const empty = initial => ({
  ...initial,
  releasePreparation: {
    factoryResetDate: moment(),
    decontaminationDate: moment(),
    cleanDate: moment(),
  },
});
